import { Link, useNavigate } from '@tanstack/react-router';
import {
  BarChart3,
  CalendarRange,
  ClipboardList,
  Clock,
  DollarSign,
  FolderKanban,
  GitBranch,
  Home,
  LogOut,
  MoreHorizontal,
  Receipt,
  Settings,
  Users,
} from 'lucide-react';
import { cn } from '@/client/lib/utils';
import { supabase } from '@/client/lib/supabase';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { trpc } from '@/client/utils/trpc';

function GaraLogo() {
  return (
    <div className="inline-flex items-center gap-2.5">
      <svg width={22} height={22} viewBox="0 0 28 28" fill="none">
        <title>Gara</title>
        <rect x="0.5" y="0.5" width="27" height="27" rx="7" className="fill-foreground" />
        <path
          d="M19.5 11.2c-.7-1.7-2.4-2.9-4.5-2.9-2.8 0-5 2.2-5 5s2.2 5 5 5c2.4 0 4.4-1.7 4.9-4h-4.5"
          className="stroke-background"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      <span className="text-sm font-semibold tracking-tight">
        Gara<span className="font-medium opacity-50">Tech</span>
      </span>
    </div>
  );
}

export function Sidebar() {
  const { data: me } = trpc.user.me.useQuery();
  const isAdmin = me?.role === 'ADMIN';
  const navigate = useNavigate();

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate({ to: '/login' });
  }

  const navItems = [
    { to: '/', label: 'Dashboard', icon: Home },
    { to: '/projects', label: 'Projects', icon: FolderKanban },
    { to: '/timesheets', label: 'My Timesheets', icon: Clock },
    { to: '/timeoff', label: 'Time Off', icon: CalendarRange },
    ...(isAdmin
      ? [
          { to: '/people', label: 'People', icon: Users },
          { to: '/timesheets/admin', label: 'All Timesheets', icon: ClipboardList },
          { to: '/timesheets/shadow', label: 'Shadow Timesheets', icon: GitBranch },
          { to: '/finance', label: 'Finance', icon: DollarSign },
          { to: '/invoices', label: 'Invoices', icon: Receipt },
          { to: '/analytics', label: 'Analytics', icon: BarChart3 },
        ]
      : []),
    { to: '/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <aside className="flex h-screen w-60 flex-col border-r bg-sidebar text-sidebar-foreground">
      <div className="flex h-14 items-center border-b px-4">
        <GaraLogo />
      </div>
      <nav className="flex-1 space-y-0.5 p-3">
        {navItems.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            activeOptions={to === '/' ? { exact: true } : undefined}
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              'hover:bg-background hover:text-foreground',
              '[&.active]:border [&.active]:border-border [&.active]:bg-background [&.active]:font-semibold [&.active]:text-foreground [&.active]:shadow-sm',
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </Link>
        ))}
      </nav>
      <div className="border-t p-3">
        <div className="flex items-center gap-2.5 rounded-md border bg-background p-2.5 shadow-sm">
          <InitialsAvatar name={me?.full_name ?? 'User'} size={32} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{me?.full_name ?? '—'}</div>
            <div className="text-xs capitalize text-muted-foreground">
              {me?.role?.toLowerCase() ?? ''}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More options"
                className="text-muted-foreground hover:text-foreground"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="end" className="w-40">
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </aside>
  );
}
